import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import { Alert, Button, Col, Drawer, Form, Input, Modal, Row, Select, Table } from 'antd';
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  SectionCard,
  StatusTag,
  TabBar,
  fmtDateTime,
  tablePagination,
  useFlatPage
} from '../components/ui';
import { IconPlus } from '../components/ui/icons';
import notify from "../utils/notify";

const { TextArea } = Input;
const { Option } = Select;

function MaintenanceRequestPage() {
  useFlatPage();
  const { authData } = useAuth();
  const [form] = Form.useForm();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [statusTab, setStatusTab] = useState('all');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchRequests();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the listed values change; the function is re-created every render and is not a trigger
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/Maintenance/GetAllMaintenanceTasks', {
        params: { hotelId: authData.hotelId }
      });
      setRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      setError(error.response?.data?.message || 'Failed to fetch requests');
      notify.error({
        title: 'Error',
        description: 'Failed to fetch maintenance requests'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomIdByRoomNumber = async (roomNumber) => {
    if (!roomNumber) return null;
    try {
      const response = await axiosInstance.get('/Rooms/GetByNumber', {
        params: { roomNumber, hotelId: authData.hotelId }
      });
      return response.data.roomId;
    } catch (error) {
      console.error('Error fetching room by number:', error);
      return null;
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const resolvedRoomId = await fetchRoomIdByRoomNumber(values.roomNumber);

      const requestBody = {
        hotelId: authData.hotelId,
        reportedBy: authData.userId,
        roomNumber: values.roomNumber,
        roomId: resolvedRoomId,
        location: values.location,
        requestedByUserId: authData.userId,
        title: values.title,
        description: values.description,
        priority: values.priority,
        status: values.status || 'Pending'
      };

      if (selectedRequest) {
        // Update existing request
        await axiosInstance.put(`/Maintenance/${selectedRequest.maintenanceTaskId}`, requestBody, {
          params: { hotelId: authData.hotelId }
        });
        notify.success({
          title: 'Success',
          description: 'Request updated successfully'
        });
      } else {
        // Create new request
        await axiosInstance.post('/Maintenance/CreateTask', requestBody);
        notify.success({
          title: 'Success',
          description: 'Request submitted successfully'
        });
      }
      
      setModalOpen(false);
      fetchRequests();
    } catch (error) {
      console.error('Failed to submit request:', error);
      setError(error.response?.data?.message || 'Failed to submit request');
      notify.error({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to submit request'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (requestId) => {
    try {
      await axiosInstance.delete(`/Maintenance/${requestId}`, {
        params: { hotelId: authData.hotelId }
      });
      notify.success({
        title: 'Success',
        description: 'Request deleted successfully'
      });
      fetchRequests();
    } catch (error) {
      console.error('Failed to delete request:', error);
      notify.error({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete request'
      });
    }
  };

  const priorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Emergency', label: 'Emergency' }
  ];

  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'InProgress', label: 'In progress' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' }
  ];

  const locationOptions = [
    'General',
    'Lobby',
    'Kitchen',
    'Restaurant',
    'Pool',
    'Gym',
    'Restroom',
    'Hallway',
    'Elevator',
    'Parking'
  ];

  const openNew = () => {
    form.resetFields();
    setSelectedRequest(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    form.setFieldsValue({ ...record, roomNumber: record.roomNumber || '' });
    setSelectedRequest(record);
    setModalOpen(true);
  };

  const openView = (record) => {
    setSelectedRequest(record);
    setViewOpen(true);
  };

  const label = (opts, v) => opts.find((o) => o.value === v)?.label || v || 'Unknown';

  const counts = useMemo(() => {
    const c = {};
    requests.forEach((r) => {
      c[r.status] = (c[r.status] || 0) + 1;
    });
    return c;
  }, [requests]);

  const tabs = [
    { key: 'all', label: 'All', count: requests.length },
    ...statusOptions.map((o) => ({ key: o.value, label: o.label, count: counts[o.value] || 0 }))
  ];

  const visibleRequests = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return requests.filter((r) => {
      if (statusTab !== 'all' && r.status !== statusTab) return false;
      if (!q) return true;
      return `${r.title || ''} ${r.description || ''} ${r.roomNumber || ''} ${r.location || ''}`.toLowerCase().includes(q);
    });
  }, [requests, statusTab, searchText]);

  const columns = [
    {
      title: 'Request',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{text}</span>
          <span className="hms-cell-sub">#{record.maintenanceTaskId}</span>
        </div>
      )
    },
    {
      title: 'Room',
      dataIndex: 'roomNumber',
      key: 'roomNumber',
      width: 110,
      render: (text) => text || <span className="hms-muted">—</span>
    },
    { title: 'Location', dataIndex: 'location', key: 'location' },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 130,
      render: (priority) => <StatusTag status={priority}>{label(priorityOptions, priority)}</StatusTag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => <StatusTag status={status}>{label(statusOptions, status)}</StatusTag>
    },
    {
      title: 'Requested',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 190,
      render: (date) => (date ? fmtDateTime(date) : <span className="hms-muted">—</span>)
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      width: 150,
      render: (_, record) => (
        <span onClick={(e) => e.stopPropagation()}>
          <RowActions
            actions={[
              { key: 'view', label: 'View request', icon: 'view', onClick: () => openView(record) },
              { key: 'edit', label: 'Edit request', icon: 'edit', onClick: () => openEdit(record) },
              {
                key: 'delete',
                label: 'Delete request',
                icon: 'delete',
                danger: true,
                confirm: 'Delete this request?',
                okText: 'Delete',
                onClick: () => handleDelete(record.maintenanceTaskId)
              }
            ]}
          />
        </span>
      )
    }
  ];

  return (
    <div className="hms-page mr-page">
      <PageHeader
        actions={
          <Button type="primary" icon={<IconPlus />} onClick={openNew}>
            New request
          </Button>
        }
      />

      {error && (
        <Alert
          title="Something went wrong"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          onClose={() => setError(null)}
        />
      )}

      <SectionCard
        flush
        title={<CardTitle count={requests.length}>Maintenance requests</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: searchText, onChange: setSearchText, placeholder: 'Search requests' }}
            onRefresh={fetchRequests}
            loading={loading}
          />
        }
      >
        <TabBar tabs={tabs} value={statusTab} onChange={setStatusTab} />

        <Table
          className="hms-table"
          columns={columns}
          dataSource={visibleRequests}
          rowKey="maintenanceTaskId"
          loading={loading}
          pagination={tablePagination('request')}
          scroll={{ x: 900 }}
          onRow={(record) => ({ onClick: () => openView(record), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <div className="hms-empty">No requests in this view</div> }}
        />
      </SectionCard>

      <Modal
        title={selectedRequest ? 'Edit maintenance request' : 'New maintenance request'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={600}
        forceRender
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ location: 'General', priority: 'Medium', status: 'Pending' }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Room number (optional)" name="roomNumber">
                <Input placeholder="e.g. 101" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Location" name="location" rules={[{ required: true, message: 'Please select a location' }]}>
                <Select placeholder="Select location">
                  {locationOptions.map((loc) => (
                    <Option key={loc} value={loc}>
                      {loc}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Priority" name="priority" rules={[{ required: true, message: 'Please select priority' }]}>
                <Select placeholder="Select priority" options={priorityOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Status" name="status" rules={[{ required: true, message: 'Please select status' }]}>
                <Select placeholder="Select status" options={statusOptions} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Please enter a title' }]}>
            <Input placeholder="Brief description of the issue" maxLength={100} />
          </Form.Item>

          <Form.Item label="Description" name="description" rules={[{ required: true, message: 'Please enter a description' }]}>
            <TextArea rows={4} placeholder="Detailed description of the problem" showCount maxLength={500} />
          </Form.Item>

          <div className="hms-form-actions">
            <Button onClick={() => setModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {selectedRequest ? 'Save changes' : 'Submit request'}
            </Button>
          </div>
        </Form>
      </Modal>

      <Drawer title="Request details" open={viewOpen} onClose={() => setViewOpen(false)} size={520} destroyOnHidden>
        {selectedRequest && (
          <>
            <div className="hms-summary">
              <div>
                <div className="hms-summary-title">{selectedRequest.title}</div>
                <div className="hms-summary-sub">Request #{selectedRequest.maintenanceTaskId}</div>
              </div>
              <StatusTag status={selectedRequest.status}>{label(statusOptions, selectedRequest.status)}</StatusTag>
            </div>

            <DetailList
              items={[
                {
                  label: 'Priority',
                  value: <StatusTag status={selectedRequest.priority}>{label(priorityOptions, selectedRequest.priority)}</StatusTag>
                },
                { label: 'Description', value: selectedRequest.description },
                { label: 'Room', value: selectedRequest.roomNumber },
                { label: 'Location', value: selectedRequest.location },
                { label: 'Requested', value: selectedRequest.createdAt ? fmtDateTime(selectedRequest.createdAt) : null }
              ]}
            />

            <div className="hms-form-actions" style={{ marginTop: 20 }}>
              <Button
                onClick={() => {
                  setViewOpen(false);
                  openEdit(selectedRequest);
                }}
              >
                Edit request
              </Button>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}

export default MaintenanceRequestPage;
